<script>
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import TrackingCategorySlots from '../../components/settings/TrackingCategorySlots.svelte';
  import { onMount } from 'svelte';
  import { settingsStore } from '../../stores/settingsStore.js';
  import { user } from '../../stores/authStore.js';
  import { loadJobCustomFields } from '../../lib/settings/jobCustomFields.js';
  import { legacyFromSlot1 } from '../../lib/trackingSlots.js';
  import { DUE_TERM_PRESETS, normalizeDueTerms, presetKeyForTerms, describeDueTerms, computeDueDate } from '../../lib/dueTerms.js';

  const {
    assignmentLensDefault,
    savingAssignmentLens,
    seeTeamUploads,
    savingSeeTeamUploads,
    billSettings,
    trackingCategories,
    loadingTrackingCategories,
    trackingCategoriesError,
    xeroBankAccounts,
    loadingBankAccounts,
    vendorAliasesList,
    loadingVendorAliases,
    vendorAliasSuccess,
    error,
    success,
    managedByOwner,
    saveAssignmentLensDefault,
    saveSeeTeamUploads,
    performSaveWithToast,
    saveBillSettings,
    loadTrackingCategories,
    fetchXeroBankAccounts,
    fetchVendorAliases,
    deleteVendorAlias,
    updateBankAccountName
  } = settingsStore;

  // Inbound email-to-bill: a dedicated random address that auto-extracts forwarded
  // bills into the review queue. Off by default; the token is a 128-bit secret.
  let inboundLoading = true;
  let inboundEnabled = false;
  let inboundAddress = null;
  let inboundSharedFromOwner = false;
  let inboundBusy = false;
  let inboundCopied = false;
  let inboundError = null;

  let jobCustomFields = [];
  let loadingCustomFields = false;
  let customFieldsError = null;

  function onBillSlotsChange(event) {
    const tracking = event.detail;
    billSettings.update((current) => ({
      ...current,
      ...legacyFromSlot1(tracking, 'jtBillNameField'),
      tracking
    }));
  }

  // Default due date for direct-import bills.
  $: dueTerms = normalizeDueTerms($billSettings.directBillDueTerms);
  $: dueTermsPreset = presetKeyForTerms(dueTerms);
  // Custom presets stay selected while the number is edited to a preset value.
  let dueTermsCustomKey = null;
  $: dueTermsSelectValue = dueTermsCustomKey || dueTermsPreset;
  $: dueTermsIsCustom = dueTermsSelectValue === 'customDays' || dueTermsSelectValue === 'customDom';
  const todayYmd = new Date().toISOString().slice(0, 10);
  $: dueTermsExample = computeDueDate(todayYmd, dueTerms);

  function saveDueTerms(next, message = '✅ Default due date updated!') {
    $billSettings.directBillDueTerms = normalizeDueTerms(next);
    performSaveWithToast(message);
  }

  function onDueTermsPresetChange(event) {
    const key = event.currentTarget.value;
    const preset = DUE_TERM_PRESETS.find(p => p.key === key);
    if (!preset) return;
    dueTermsCustomKey = key === 'customDays' || key === 'customDom' ? key : null;
    const keepCustomValue = (key === 'customDays' && dueTerms.type === 'days')
      || (key === 'customDom' && dueTerms.type === 'followingMonth');
    saveDueTerms(keepCustomValue ? dueTerms : { ...preset.terms, override: dueTerms.override });
  }

  function onDueTermsNumberChange(event) {
    const n = Number(event.currentTarget.value);
    saveDueTerms(dueTerms.type === 'days'
      ? { ...dueTerms, days: n }
      : { ...dueTerms, dayOfMonth: n });
  }

  async function loadInboundEmail() {
    try {
      const res = await fetch('/api/users/inbound-email');
      if (res.ok) {
        const data = await res.json();
        inboundEnabled = data.enabled;
        inboundAddress = data.address;
        inboundSharedFromOwner = data.sharedFromOwner === true;
      }
    } catch (err) {
      console.error('Failed to load inbound email settings:', err);
    } finally {
      inboundLoading = false;
    }
  }

  function flashInboundError(message) {
    inboundError = message;
    setTimeout(() => { inboundError = null; }, 5000);
  }

  async function toggleInbound() {
    if (inboundSharedFromOwner) return;
    inboundBusy = true;
    const next = !inboundEnabled;
    try {
      const res = await fetch('/api/users/inbound-email', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        inboundEnabled = data.enabled;
        inboundAddress = data.address;
        inboundError = null;
        performSaveWithToast(next ? '✅ Inbound email enabled!' : '✅ Inbound email disabled');
      } else {
        flashInboundError(data.error || 'Failed to update inbound email setting.');
      }
    } catch (err) {
      console.error('Failed to toggle inbound email:', err);
      flashInboundError('Failed to update inbound email setting.');
    } finally {
      inboundBusy = false;
    }
  }

  async function regenerateInbound() {
    if (inboundSharedFromOwner) return;
    if (inboundAddress && !confirm('Generate a new address? The old one will stop working immediately and must be re-shared with anyone who emails bills.')) return;
    inboundBusy = true;
    try {
      const res = await fetch('/api/users/inbound-email/rotate', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        inboundEnabled = data.enabled;
        inboundAddress = data.address;
        inboundError = null;
        performSaveWithToast('✅ New inbound address generated!');
      } else {
        flashInboundError(data.error || 'Failed to generate inbound email address.');
      }
    } catch (err) {
      console.error('Failed to regenerate inbound address:', err);
      flashInboundError('Failed to generate inbound email address.');
    } finally {
      inboundBusy = false;
    }
  }

  async function copyInbound() {
    if (!inboundAddress) return;
    try {
      await navigator.clipboard.writeText(inboundAddress);
      inboundCopied = true;
      setTimeout(() => { inboundCopied = false; }, 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }

  onMount(async () => {
    fetchXeroBankAccounts();
    fetchVendorAliases();
    loadInboundEmail();
    loadingCustomFields = true;
    customFieldsError = null;
    try {
      jobCustomFields = await loadJobCustomFields();
    } catch (err) {
      customFieldsError = err.message || 'Failed to load custom fields';
      jobCustomFields = [];
    } finally {
      loadingCustomFields = false;
    }
    try {
      await loadTrackingCategories();
    } catch {
      // The slot component shows trackingCategoriesError.
    }
  });
</script>

<SettingsLayout activeTab="bill-sync" tabTitle="Bill Settings">
  <div class="space-y-8">
    <!-- Bill Assignment Defaults -->
    <section>
      <h4 class="text-base font-semibold text-gray-900 mb-3">
        Bill Assignment Defaults
        <span class="text-sm font-normal text-gray-500 ml-2">
          Search by {$assignmentLensDefault === 'costCode' ? 'Cost Code' : 'Cost Group'}
        </span>
      </h4>
      <p class="text-sm text-gray-700 mb-3">
        When importing bills from Xero, the assignment search box on the bill sync page will default to this view.
        You can still flip between the two views on the page itself per bill.
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          on:click={() => saveAssignmentLensDefault('costGroup')}
          disabled={$savingAssignmentLens}
          class="text-left p-4 border-2 rounded-md transition-colors {$assignmentLensDefault === 'costGroup' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-400'} disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="font-semibold text-sm text-gray-900">Cost Group</span>
            {#if $assignmentLensDefault === 'costGroup'}
              <span class="text-xs font-bold text-blue-600">SELECTED</span>
            {/if}
          </div>
          <p class="text-xs text-gray-600">
            Search by JobTread budget structure — e.g. "MAIN CONTRACT › 7- EXTERIOR CLADDING › ROOF".
            Best when you think in terms of the original quote/proposal sections.
          </p>
        </button>
        <button
          type="button"
          on:click={() => saveAssignmentLensDefault('costCode')}
          disabled={$savingAssignmentLens}
          class="text-left p-4 border-2 rounded-md transition-colors {$assignmentLensDefault === 'costCode' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-400'} disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="font-semibold text-sm text-gray-900">Cost Code</span>
            {#if $assignmentLensDefault === 'costCode'}
              <span class="text-xs font-bold text-blue-600">SELECTED</span>
            {/if}
          </div>
          <p class="text-xs text-gray-600">
            Search by JobTread cost code number — e.g. "0106 Geotech Engineer Design".
            Best for bookkeepers reconciling bills against the chart of accounts.
          </p>
        </button>
      </div>
    </section>

    <!-- Owner-managed sync settings: disabled for non-owner members (slaved to owner). -->
    <fieldset class="contents" disabled={$managedByOwner}>
    <!-- Xero to Jobtread Bill Settings -->
    <section class="border-t border-gray-200 pt-6">
      <h4 class="text-base font-semibold text-gray-900 mb-4">Xero to Jobtread Bill Settings</h4>

      <div class="space-y-6">
        <div>
          <h5 class="text-sm font-semibold text-gray-900 mb-2">
            Bill Mode Default
            <span class="text-xs font-normal text-gray-500 ml-2">{$billSettings.billmodedefaultjob ? 'Job Mode' : 'PO Mode'}</span>
          </h5>
          <div class="p-3 bg-blue-50 border border-blue-200 rounded-md mb-3">
            <p class="text-sm text-blue-800">
              📖 <strong>Bill Mode Default:</strong><br>
              Choose the default mode for syncing bills. PO Mode links bills to purchase orders, while Job Mode links bills directly to jobs.
            </p>
          </div>
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
            <div>
              <label for="billModeToggle" class="block text-sm font-medium text-gray-700">Default Bill Sync Mode</label>
              <p class="text-xs text-gray-500 mt-1">Select how bills should be synced by default</p>
            </div>
            <div class="flex items-center gap-3">
              <span class="text-sm font-medium {!$billSettings.billmodedefaultjob ? 'text-blue-600' : 'text-gray-500'}">PO Mode</span>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  id="billModeToggle"
                  type="checkbox"
                  bind:checked={$billSettings.billmodedefaultjob}
                  on:change={() => performSaveWithToast('✅ Bill mode updated successfully!')}
                  class="sr-only peer"
                />
                <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span class="text-sm font-medium {$billSettings.billmodedefaultjob ? 'text-blue-600' : 'text-gray-500'}">Job Mode</span>
            </div>
          </div>
        </div>

        <div>
          <h5 class="text-sm font-semibold text-gray-900 mb-2">Don't automatically approve Xero Bills on import to JobTread</h5>
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
            <div class="pr-4">
              <p class="text-sm font-medium text-gray-700">Don't automatically approve Xero Bills on import to JobTread</p>
              <p class="text-xs text-gray-500 mt-1">
                When enabled, imported bills will be left in draft status in JobTread instead of being automatically set to pending/approved.
              </p>
            </div>
            <div class="flex items-center gap-3 flex-shrink-0">
              <span class="text-sm font-bold {$billSettings.skipAutoApprove ? 'text-green-600' : 'text-red-600'}">
                {$billSettings.skipAutoApprove ? 'ON' : 'OFF'}
              </span>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={$billSettings.skipAutoApprove}
                  on:change={() => performSaveWithToast('✅ Bill approval setting updated!')}
                  class="sr-only peer"
                />
                <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
          </div>
        </div>

        <div>
          <h5 class="text-sm font-semibold text-gray-900 mb-2">Mark Direct import Bills as Paid</h5>
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
            <div class="pr-4">
              <p class="text-sm font-medium text-gray-700">Mark Direct import Bills as Paid</p>
              <p class="text-xs text-gray-500 mt-1">
                When enabled, the Mark as Paid checkbox on Direct Bill import defaults to on. Imported bills are marked paid in Xero and JobTread after sync. You can still override per bill on the review screen.
              </p>
            </div>
            <div class="flex items-center gap-3 flex-shrink-0">
              <span class="text-sm font-bold {$billSettings.markDirectImportBillsAsPaid ? 'text-green-600' : 'text-red-600'}">
                {$billSettings.markDirectImportBillsAsPaid ? 'ON' : 'OFF'}
              </span>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={$billSettings.markDirectImportBillsAsPaid}
                  on:change={() => performSaveWithToast('✅ Mark Direct import Bills as Paid setting updated!')}
                  class="sr-only peer"
                />
                <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </section>

    </fieldset>

    <!-- Direct Bills. The team-uploads toggle sits outside the owner-locked
         fieldset so a non-owner can turn it on for themselves. -->
    <section class="border-t border-gray-200 pt-6">
      <h4 class="text-base font-semibold text-gray-900 mb-4">Direct Bills</h4>
      {#if $user?.orgId}
        <div class="flex items-center justify-between p-4 bg-white rounded-md border border-gray-200 mb-4">
          <div class="pr-4">
            <p class="text-sm font-medium text-gray-700">See other team members' uploads</p>
            <p class="text-xs text-gray-500 mt-1">
              When on, Direct Bills also lists uploads from other people in your organisation, so someone in the office can import bills that were uploaded in the field. Your own uploads are unchanged.
            </p>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <span class="text-sm font-bold {$seeTeamUploads ? 'text-green-600' : 'text-red-600'}">
              {$seeTeamUploads ? 'ON' : 'OFF'}
            </span>
            <label class="relative inline-flex items-center {$savingSeeTeamUploads ? 'cursor-wait' : 'cursor-pointer'}">
              <input
                type="checkbox"
                checked={$seeTeamUploads}
                on:change={(e) => saveSeeTeamUploads(e.currentTarget.checked)}
                disabled={$savingSeeTeamUploads}
                class="sr-only peer"
              />
              <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>
      {/if}
      <fieldset class="contents" disabled={$managedByOwner}>
      <!-- Default due date (direct bill import) -->
      <div class="p-4 bg-white rounded-md border border-gray-200 mb-4">
        <p class="text-sm font-medium text-gray-700">Default due date</p>
        <p class="text-xs text-gray-500 mt-1">
          Fills in the due date on imported bills that don't show one, worked out from the invoice date. You can still change it per bill on the review screen.
        </p>
        <div class="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <select
            value={dueTermsSelectValue}
            on:change={onDueTermsPresetChange}
            class="w-full sm:w-72 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {#each DUE_TERM_PRESETS as preset}
              <option value={preset.key}>{preset.label}</option>
            {/each}
          </select>
          {#if dueTermsIsCustom && dueTerms.type === 'days'}
            <label class="flex items-center gap-2 text-sm text-gray-700">
              <input type="number" min="0" max="365" value={dueTerms.days} on:change={onDueTermsNumberChange} class="w-20 px-2 py-2 text-sm border border-gray-300 rounded-md" />
              days after invoice date
            </label>
          {:else if dueTermsIsCustom && dueTerms.type === 'followingMonth'}
            <label class="flex items-center gap-2 text-sm text-gray-700">
              Day
              <input type="number" min="1" max="31" value={dueTerms.dayOfMonth} on:change={onDueTermsNumberChange} class="w-20 px-2 py-2 text-sm border border-gray-300 rounded-md" />
              of the following month
            </label>
          {/if}
        </div>
        {#if dueTerms.type !== 'none'}
          <p class="text-xs text-gray-500 mt-2">
            {describeDueTerms(dueTerms)} — e.g. a bill dated today would be due <span class="font-medium text-gray-700">{dueTermsExample}</span>.
          </p>
          <label class="mt-3 flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              class="mt-0.5 h-4 w-4"
              checked={dueTerms.override}
              on:change={(e) => saveDueTerms({ ...dueTerms, override: e.currentTarget.checked })}
            />
            <span>
              Always use these terms
              <span class="block text-xs text-gray-500">Replaces the due date printed on the supplier's bill. Leave off to only fill in bills with no due date.</span>
            </span>
          </label>
        {/if}
      </div>

      <!-- Multiple currency support (direct bill import) -->
      <div class="flex items-center justify-between p-4 bg-white rounded-md border border-gray-200">
        <div class="pr-4">
          <p class="text-sm font-medium text-gray-700">Multiple currency support</p>
          <p class="text-xs text-gray-500 mt-1">
            When enabled, uploaded bills have their currency detected from the document (and can be changed on the review screen). Each bill syncs to Xero in its own currency, and to JobTread converted into your default currency using Xero's exchange rate. Leave off if all your bills are in your default currency.
          </p>
        </div>
        <div class="flex items-center gap-3 flex-shrink-0">
          <span class="text-sm font-bold {$billSettings.multiCurrencyEnabled ? 'text-green-600' : 'text-red-600'}">
            {$billSettings.multiCurrencyEnabled ? 'ON' : 'OFF'}
          </span>
          <label class="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              bind:checked={$billSettings.multiCurrencyEnabled}
              on:change={() => performSaveWithToast('✅ Multiple currency support updated!')}
              class="sr-only peer"
            />
            <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
          </label>
        </div>
      </div>

      <!-- Inbound email-to-bill -->
      <div class="p-4 bg-white rounded-md border border-gray-200 mt-4 {inboundSharedFromOwner && !inboundEnabled ? 'opacity-60' : ''}">
        <div class="flex items-center justify-between">
          <div class="pr-4">
            <p class="text-sm font-medium text-gray-700">Email bills in</p>
            <p class="text-xs text-gray-500 mt-1">
              {#if inboundSharedFromOwner}
                Bills emailed to your organisation's address land in every member's Direct Bills review queue. Only the owner can turn this on or change the address.
              {:else}
                Get a private email address for this account. Forward a bill to it — or have your suppliers send bills straight to it — and it's automatically run through the same extraction as a manual upload, landing in your Direct Bills review queue. Nothing syncs to Xero or JobTread without your review. Keep this address private: anyone who has it can drop bills into your queue.
              {/if}
            </p>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <span class="text-sm font-bold {inboundEnabled ? 'text-green-600' : 'text-red-600'}">
              {inboundEnabled ? 'ON' : 'OFF'}
            </span>
            <label class="relative inline-flex items-center {inboundSharedFromOwner ? 'cursor-not-allowed' : 'cursor-pointer'}">
              <input
                type="checkbox"
                checked={inboundEnabled}
                on:change={toggleInbound}
                disabled={inboundLoading || inboundBusy || inboundSharedFromOwner}
                class="sr-only peer"
              />
              <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500 {inboundSharedFromOwner ? 'opacity-50' : ''}"></div>
            </label>
          </div>
        </div>

        {#if inboundSharedFromOwner && !inboundEnabled && !inboundLoading}
          <p class="mt-3 text-xs text-gray-500">The owner has not enabled email bills in yet.</p>
        {/if}

        {#if inboundEnabled && inboundAddress}
          <div class="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <code class="flex-1 text-sm bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-gray-800 break-all">{inboundAddress}</code>
            <div class="flex gap-2 flex-shrink-0">
              <button
                type="button"
                on:click={copyInbound}
                class="px-3 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {inboundCopied ? '✓ Copied' : 'Copy'}
              </button>
              {#if !inboundSharedFromOwner}
                <button
                  type="button"
                  on:click={regenerateInbound}
                  disabled={inboundBusy}
                  class="px-3 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Regenerate
                </button>
              {/if}
            </div>
          </div>
        {/if}

        {#if inboundError}
          <div class="mt-3 p-3 bg-red-50 border border-red-300 rounded-md">
            <p class="text-sm text-red-800">❌ <strong>Error:</strong> {inboundError}</p>
          </div>
        {/if}
      </div>
      </fieldset>
    </section>

    <fieldset class="contents" disabled={$managedByOwner}>
    <!-- Jobtread to Xero Sync Settings -->
    <section class="border-t border-gray-200 pt-6">
      <h4 class="text-base font-semibold text-gray-900 mb-4">Jobtread to Xero Sync Settings</h4>
      <div class="space-y-4">
        <div class="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div class="text-sm text-blue-800">
            <p>📖 <strong>Jobtread → Xero Sync:</strong></p>
            <p class="mt-1">This feature is for users that want to use a custom Xero Tracking Category that will be used during syncs to:</p>
            <ul class="list-disc list-inside mt-2 ml-2 space-y-1">
              <li>When you are importing a bill from Xero to Jobtread this Category code will be used to look for the Job Name that you have entered into the bill in Xero.</li>
              <li>When you are importing a bill or expense from Jobtread to Xero this Tracking category will be used to store the Job Name and Job Number in Xero.</li>
            <li>You can send a second JobTread field to a second Xero category, for example Job Name and Division.</li>
            </ul>
          </div>
        </div>

        <TrackingCategorySlots
          slots={$billSettings.tracking}
          {jobCustomFields}
          {loadingCustomFields}
          {customFieldsError}
          categories={$trackingCategories}
          loadingCategories={$loadingTrackingCategories}
          categoriesError={$trackingCategoriesError}
          idPrefix="bill"
          on:change={onBillSlotsChange}
          on:save={saveBillSettings}
          on:loadCategories={loadTrackingCategories}
        />

        {#if $success && $success.includes('Bill tracking')}
          <div class="p-3 bg-green-50 border border-green-300 rounded-md">
            <p class="text-sm text-green-800">{$success}</p>
          </div>
        {/if}

        {#if $error && !$error.startsWith('[INVOICE]') && ($error.includes('Tracking category') || $error.includes('tracking') || $error.includes('Bill settings') || $error.includes('bill settings'))}
          <div class="p-3 bg-red-50 border border-red-300 rounded-md">
            <p class="text-sm text-red-800">❌ <strong>Error:</strong> {$error}</p>
          </div>
        {/if}

        <div>
          <label for="xeroExpenseBankAccount" class="block text-sm font-medium text-gray-700 mb-2">Xero Bank Account for Expenses</label>
          <select
            id="xeroExpenseBankAccount"
            bind:value={$billSettings.xeroExpenseBankAccountID}
            on:focus={() => { if ($xeroBankAccounts.length === 0) fetchXeroBankAccounts(); }}
            on:change={() => updateBankAccountName($billSettings.xeroExpenseBankAccountID)}
            disabled={$billSettings.xeroExpenseSyncMode !== 'spend' && !$billSettings.xeroExpenseAutoPayment}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">-- Default (first bank account in Xero) --</option>
            {#if $loadingBankAccounts}
              <option disabled>Loading...</option>
            {:else}
              {#each $xeroBankAccounts as acc}
                <option value={acc.accountID}>{acc.name}</option>
              {/each}
            {/if}
          </select>
          <p class="mt-1 text-xs text-gray-500">
            {#if $billSettings.xeroExpenseSyncMode === 'spend'}
              Bank account that spend money transactions will be recorded against in Xero.
            {:else if !$billSettings.xeroExpenseAutoPayment}
              Not applicable — automatic payment creation is disabled.
            {:else}
              Bank account used when creating payments in Xero.
            {/if}
          </p>
        </div>

        <div class="p-4 bg-white border border-gray-200 rounded-md">
          <label for="xeroExpenseSyncMode" class="block text-sm font-medium text-gray-700 mb-1">Expense Sync Mode</label>
          <p class="text-xs text-gray-500 mb-3">
            Choose how expenses are created in Xero. "Spend Money" transactions do not count towards the Xero Starter plan's invoice approval limit.
          </p>
          <select
            id="xeroExpenseSyncMode"
            bind:value={$billSettings.xeroExpenseSyncMode}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="bill">Bill + Payment (default)</option>
            <option value="spend">Spend Money Transaction</option>
          </select>
        </div>

        {#if $billSettings.xeroExpenseSyncMode !== 'spend'}
          <div class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-md">
            <div>
              <p class="text-sm font-medium text-gray-700">Create payment automatically</p>
              <p class="text-xs text-gray-500 mt-1">
                When enabled, a payment is created in Xero and applied to the Xero bill immediately, marking the expense as paid.
                Disable this setting to create only the expense/bill in Xero. Disable this if you reconcile bills to transactions received from bank feeds in Xero.
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0">
              <input type="checkbox" bind:checked={$billSettings.xeroExpenseAutoPayment} class="sr-only peer" />
              <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        {/if}

        <div class="border-t border-gray-200 pt-4 mt-4">
          <div class="flex items-center justify-between mb-2">
            <h5 class="text-sm font-medium text-gray-700">Saved Vendor Aliases</h5>
            <button type="button" class="text-xs text-blue-600 hover:text-blue-800" on:click={fetchVendorAliases}>
              {$loadingVendorAliases ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          <p class="text-xs text-gray-500 mb-3">
            These are vendor name mappings you've confirmed during bill imports. When the same vendor name appears again, it will be matched automatically.
          </p>

          {#if $vendorAliasSuccess}
            <div class="mb-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded text-xs text-green-700">
              {$vendorAliasSuccess}
            </div>
          {/if}

          {#if $vendorAliasesList.length === 0 && !$loadingVendorAliases}
            <p class="text-xs text-gray-400 italic">No vendor aliases saved yet. They will appear here as you confirm vendor matches during bill imports.</p>
          {:else}
            <div class="space-y-1 max-h-48 overflow-y-auto">
              {#each $vendorAliasesList as alias}
                <div class="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                  <div class="text-xs">
                    <span class="text-gray-700 font-medium">{alias.sourceName}</span>
                    <span class="text-gray-400 mx-1">&rarr;</span>
                    <span class="text-gray-500">{alias.jtVendorId}</span>
                  </div>
                  <button
                    type="button"
                    class="ml-2 text-red-400 hover:text-red-600 p-0.5"
                    title="Delete alias"
                    on:click={() => deleteVendorAlias(alias.sourceName)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    </section>
    </fieldset>
  </div>
</SettingsLayout>
